import React, { useMemo } from 'react'
import { Line } from 'react-chartjs-2'

import {
  CategoryScale,
  Chart as ChartJS,
  ChartData,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip
} from 'chart.js'
import gradient from 'chartjs-plugin-gradient'

import { NavigationWrapper } from 'components/AccountEngagementTable'
import { AccountActivity, AccountScoredActivity } from 'modules/accountActivity/domain'
import { getEngagementScoreColor } from 'utils/activity'

import styles from './styles.module.css'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  gradient
)

export const options = {
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false
    }
  },
  scales: {
    y: {
      display: false,
      grid: {
        display: false
      },
      ticks: {
        display: true
      },
      beginAtZero: true,
      min: 0,
      suggestedMax: 5
    },
    x: {
      display: false,
      grid: {
        display: false
      },
      ticks: {
        display: false
      }
    }
  },
  layout: {
    padding: 0
  }
}

export const ScoreChart = ({
  score,
  accountActivity
}: {
  score?: AccountScoredActivity
  accountActivity: AccountActivity
}) => {
  const chartData: ChartData<'line', number[], string> = useMemo(() => {
    if (!score?.historicalScores || !score?.normalizedScore) {
      return {
        datasets: []
      }
    }

    let engagementNumber = score.normalizedScore
    if (engagementNumber < 0) engagementNumber = 0
    const scoreNumber = Math.min(Math.round(engagementNumber), 5)

    return {
      labels: score.historicalScores?.map(() => ''),
      datasets: [
        {
          data: score.historicalScores,
          gradient: {
            backgroundColor: {
              axis: 'x',
              colors: {
                0: '#ffffff',
                100: `${getEngagementScoreColor(scoreNumber)}c2`
              }
            }
          },
          borderColor: getEngagementScoreColor(scoreNumber),
          borderWidth: 1,
          tension: 0.4,
          pointRadius: 0,
          animation: false,
          fill: true
        }
      ]
    }
  }, [score?.normalizedScore, score?.historicalScores])

  return (
    <NavigationWrapper
      accountId={accountActivity.accountId}
      accountName={accountActivity.companyData?.name}>
      <div className={styles.scoreChartContainer}>
        {chartData && <Line options={options} data={chartData} />}
      </div>
    </NavigationWrapper>
  )
}

export default ScoreChart
